/* ═══════════════════════════════════════════════════
   CAREER DISCOVERY QUIZ — v2 (Adventure Style)
   
   Instead of boring "What do you like?" questions,
   students explore different worlds and choose activities.
   Each world tests specific trait combinations.
   
   35 Questions × 5 Worlds:
   🎨 Creative Studio (7 Qs) 
   🔬 Science Lab (7 Qs)
   🤝 Social Hub (7 Qs)
   🔧 Tech Workshop (7 Qs)
   💼 Business Arena (7 Qs)
═══════════════════════════════════════════════════ */

const quizQuestions = [
  // ═══════════ 🎨 CREATIVE STUDIO (1-7) ═══════════
  {
    id: 1,
    question:
      "You walk into a Creative Studio. Paints, cameras, instruments everywhere. What catches your eye first?",
    emoji: "🎨",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "The drawing tablets and design software",
        traits: { creative: 5, technical: 2 },
      },
      {
        text: "The data charts on the whiteboard",
        traits: { analytical: 5, business: 1 },
      },
      {
        text: "The group brainstorming at the table",
        traits: { social: 4, creative: 1 },
      },
      {
        text: "The 3D printer building a robot arm",
        traits: { technical: 5, analytical: 1 },
      },
    ],
  },
  {
    id: 2,
    question:
      "The studio gives you a blank canvas and 1 hour. What do you create?",
    emoji: "🖼️",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "A colorful abstract painting or illustration",
        traits: { creative: 5 },
      },
      {
        text: "A mind map connecting complex ideas",
        traits: { analytical: 4, creative: 1 },
      },
      {
        text: "A poster promoting a social cause I care about",
        traits: { social: 4, creative: 2 },
      },
      {
        text: "A business plan sketch for my startup idea",
        traits: { business: 5, creative: 1 },
      },
    ],
  },
  {
    id: 3,
    question:
      "A filmmaker needs help with their short film. Which role do you pick?",
    emoji: "🎬",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "Director — I want to control the vision",
        traits: { creative: 5, business: 1 },
      },
      {
        text: "Editor — I'll perfect every frame technically",
        traits: { technical: 4, creative: 2 },
      },
      {
        text: "Script writer — I'll craft the story",
        traits: { creative: 4, social: 2 },
      },
      {
        text: "Producer — I'll manage budget and schedule",
        traits: { business: 5, social: 1 },
      },
    ],
  },
  {
    id: 4,
    question:
      "You're redesigning your school's website. What matters most to you?",
    emoji: "💻",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "Making it visually stunning with great UX",
        traits: { creative: 5, technical: 1 },
      },
      {
        text: "Making the code clean and fast-loading",
        traits: { technical: 5, analytical: 1 },
      },
      {
        text: "Making it useful for all students and parents",
        traits: { social: 4, business: 1 },
      },
      {
        text: "Adding analytics to track user behavior",
        traits: { analytical: 5, business: 1 },
      },
    ],
  },
  {
    id: 5,
    question:
      "A fashion brand asks you to help with their next collection. You'd love to…",
    emoji: "👗",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "Design the clothing and visual aesthetic",
        traits: { creative: 5 },
      },
      {
        text: "Analyze market trends to find what sells",
        traits: { analytical: 3, business: 3 },
      },
      {
        text: "Create the marketing campaign and content",
        traits: { creative: 3, business: 2, social: 1 },
      },
      { text: "Build the e-commerce website", traits: { technical: 5 } },
    ],
  },
  {
    id: 6,
    question:
      "You have ₹10,000 to spend on learning something new. You choose…",
    emoji: "📚",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      { text: "A digital art or animation course", traits: { creative: 5 } },
      { text: "A coding bootcamp", traits: { technical: 5 } },
      {
        text: "A public speaking or leadership workshop",
        traits: { social: 4, business: 2 },
      },
      {
        text: "A business and entrepreneurship program",
        traits: { business: 5 },
      },
    ],
  },
  {
    id: 7,
    question: "Your phone gallery is mostly full of…",
    emoji: "📱",
    world: "Creative Studio",
    worldColor: "#A29BFE",
    options: [
      {
        text: "Photos I edited or artistic shots I took",
        traits: { creative: 5 },
      },
      {
        text: "Screenshots of interesting articles and data",
        traits: { analytical: 5 },
      },
      { text: "Selfies and group photos with friends", traits: { social: 5 } },
      {
        text: "Product ideas and business screenshots",
        traits: { business: 4, technical: 1 },
      },
    ],
  },

  // ═══════════ 🔬 SCIENCE LAB (8-14) ═══════════
  {
    id: 8,
    question:
      "You enter the Science Lab. Microscopes, chemicals, circuit boards everywhere. You're drawn to…",
    emoji: "🔬",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "The microscope — examining tiny structures",
        traits: { analytical: 5, technical: 1 },
      },
      {
        text: "The art supplies — designing a science poster",
        traits: { creative: 4, analytical: 1 },
      },
      {
        text: "The group discussing a breakthrough theory",
        traits: { social: 3, analytical: 3 },
      },
      {
        text: "The computer running complex simulations",
        traits: { technical: 5, analytical: 1 },
      },
    ],
  },
  {
    id: 9,
    question:
      "Your science teacher assigns a project on climate change. Your approach?",
    emoji: "🌍",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Collect and analyze real climate data",
        traits: { analytical: 5 },
      },
      {
        text: "Create a documentary or animation about it",
        traits: { creative: 5, social: 1 },
      },
      { text: "Organize a school awareness campaign", traits: { social: 5 } },
      {
        text: "Build a working model of a green solution",
        traits: { technical: 5, analytical: 1 },
      },
    ],
  },
  {
    id: 10,
    question: "If you discovered a new species of insect, you'd…",
    emoji: "🐛",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Study its DNA and classify it scientifically",
        traits: { analytical: 5 },
      },
      { text: "Draw detailed illustrations of it", traits: { creative: 5 } },
      {
        text: "Tell everyone about it — share the discovery",
        traits: { social: 4, business: 1 },
      },
      {
        text: "Figure out if it has commercial applications",
        traits: { business: 5 },
      },
    ],
  },
  {
    id: 11,
    question: "Your favourite type of YouTube content is…",
    emoji: "📺",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Science explainers (Veritasium, Kurzgesagt)",
        traits: { analytical: 5 },
      },
      { text: "Art and design tutorials", traits: { creative: 5 } },
      {
        text: "Vlogs and motivational speakers",
        traits: { social: 4, business: 1 },
      },
      { text: "Tech reviews and coding tutorials", traits: { technical: 5 } },
    ],
  },
  {
    id: 12,
    question: "A maths problem seems impossible. Your reaction?",
    emoji: "🧮",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Break it into steps and grind until solved",
        traits: { analytical: 5, technical: 1 },
      },
      {
        text: "Try to visualize it differently",
        traits: { creative: 4, analytical: 1 },
      },
      {
        text: "Ask a friend to work through it together",
        traits: { social: 5 },
      },
      {
        text: "Look for a shortcut or practical trick",
        traits: { business: 3, technical: 2 },
      },
    ],
  },
  {
    id: 13,
    question: "If you could have any superpower, it would be…",
    emoji: "⚡",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Super intelligence — solve any problem",
        traits: { analytical: 5 },
      },
      { text: "Create anything from imagination", traits: { creative: 5 } },
      { text: "Read minds — understand everyone", traits: { social: 5 } },
      {
        text: "Time travel — see the future and plan",
        traits: { business: 4, analytical: 1 },
      },
    ],
  },
  {
    id: 14,
    question:
      "The school picks you for a NASA space mission competition. Your role?",
    emoji: "🚀",
    world: "Science Lab",
    worldColor: "#6C5CE7",
    options: [
      {
        text: "Lead researcher — design the experiment",
        traits: { analytical: 5, technical: 1 },
      },
      {
        text: "Visual designer — create the presentation",
        traits: { creative: 5 },
      },
      {
        text: "Team captain — motivate and manage everyone",
        traits: { social: 4, business: 2 },
      },
      { text: "Engineer — build the prototype", traits: { technical: 5 } },
    ],
  },

  // ═══════════ 🤝 SOCIAL HUB (15-21) ═══════════
  {
    id: 15,
    question:
      "You enter the Social Hub — a buzzing community center. People are working, learning, helping. You join…",
    emoji: "🤝",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      {
        text: "The mentoring circle helping younger students",
        traits: { social: 5 },
      },
      {
        text: "The research group studying community problems",
        traits: { analytical: 4, social: 2 },
      },
      {
        text: "The design team creating awareness posters",
        traits: { creative: 4, social: 1 },
      },
      {
        text: "The startup pitch session for social enterprises",
        traits: { business: 4, social: 2 },
      },
    ],
  },
  {
    id: 16,
    question: "A friend comes with a serious personal problem. You naturally…",
    emoji: "💬",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      { text: "Listen patiently and comfort them", traits: { social: 5 } },
      {
        text: "Analyze the problem and suggest a logical plan",
        traits: { analytical: 4, social: 1 },
      },
      {
        text: "Share a story or creative metaphor to help",
        traits: { creative: 3, social: 2 },
      },
      {
        text: "Help them make a step-by-step action plan",
        traits: { business: 3, social: 2 },
      },
    ],
  },
  {
    id: 17,
    question:
      "You're organizing an event for 500 people. What excites you most?",
    emoji: "🎉",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      {
        text: "Meeting all the guests and making connections",
        traits: { social: 5, business: 1 },
      },
      {
        text: "Planning the perfect schedule and logistics",
        traits: { analytical: 3, business: 3 },
      },
      {
        text: "Designing the theme, decorations, and vibe",
        traits: { creative: 5 },
      },
      {
        text: "Setting up the sound, lighting, and tech",
        traits: { technical: 5 },
      },
    ],
  },
  {
    id: 18,
    question: "Your ideal weekend activity is…",
    emoji: "☀️",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      {
        text: "Volunteering or helping in the community",
        traits: { social: 5 },
      },
      {
        text: "Working on a personal project or experiment",
        traits: { technical: 3, creative: 2 },
      },
      {
        text: "Reading, researching, or learning something",
        traits: { analytical: 5 },
      },
      { text: "Planning my next business move", traits: { business: 5 } },
    ],
  },
  {
    id: 19,
    question:
      "If you could teach one class to your entire school, it would be…",
    emoji: "🧑‍🏫",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      { text: "Life skills and emotional intelligence", traits: { social: 5 } },
      {
        text: "Science experiments and discoveries",
        traits: { analytical: 4, technical: 2 },
      },
      { text: "Art, photography, or filmmaking", traits: { creative: 5 } },
      {
        text: "Money management and entrepreneurship",
        traits: { business: 5 },
      },
    ],
  },
  {
    id: 20,
    question: "Two classmates are fighting. You instinctively…",
    emoji: "🕊️",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      { text: "Step in and help them talk it out", traits: { social: 5 } },
      {
        text: "Stay neutral and observe what happens",
        traits: { analytical: 4 },
      },
      {
        text: "Write about it later — maybe a story or poem",
        traits: { creative: 4, analytical: 1 },
      },
      {
        text: "Figure out who benefits and who loses",
        traits: { business: 4, analytical: 1 },
      },
    ],
  },
  {
    id: 21,
    question: "Your friends describe you as…",
    emoji: "🌟",
    world: "Social Hub",
    worldColor: "#00CEC9",
    options: [
      { text: "The caring one who always listens", traits: { social: 5 } },
      {
        text: "The smart one who knows random facts",
        traits: { analytical: 5 },
      },
      { text: "The creative one with wild ideas", traits: { creative: 5 } },
      {
        text: "The practical one who gets things done",
        traits: { business: 3, technical: 2 },
      },
    ],
  },

  // ═══════════ 🔧 TECH WORKSHOP (22-28) ═══════════
  {
    id: 22,
    question:
      "Welcome to the Tech Workshop — robots, screens, circuit boards. You immediately want to…",
    emoji: "🔧",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      {
        text: "Open up a gadget and see how it works inside",
        traits: { technical: 5 },
      },
      {
        text: "Design a cool interface for the robot",
        traits: { creative: 4, technical: 1 },
      },
      {
        text: "Teach someone else how to use the tools",
        traits: { social: 4, technical: 1 },
      },
      { text: "Figure out how to sell these robots", traits: { business: 5 } },
    ],
  },
  {
    id: 23,
    question: "If you could build any app in 24 hours, it would…",
    emoji: "📲",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      {
        text: "Solve a real problem using smart technology",
        traits: { technical: 5, analytical: 1 },
      },
      {
        text: "Be a beautiful creative experience or game",
        traits: { creative: 5 },
      },
      { text: "Connect people and build communities", traits: { social: 5 } },
      { text: "Help people make or save money", traits: { business: 5 } },
    ],
  },
  {
    id: 24,
    question: "Your favourite type of game is…",
    emoji: "🎮",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      {
        text: "Building/crafting games (Minecraft, Factorio)",
        traits: { technical: 5, creative: 1 },
      },
      {
        text: "Strategy games (Chess, Clash of Clans)",
        traits: { analytical: 3, business: 3 },
      },
      { text: "Multiplayer/social games with friends", traits: { social: 5 } },
      { text: "Creative sandbox and design games", traits: { creative: 5 } },
    ],
  },
  {
    id: 25,
    question: "Something breaks at home. Your first instinct?",
    emoji: "🔧",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      { text: "Open it up and try to fix it myself", traits: { technical: 5 } },
      {
        text: "Google the problem and analyze solutions",
        traits: { analytical: 4, technical: 1 },
      },
      {
        text: "Call someone who knows how to fix it",
        traits: { social: 3, business: 1 },
      },
      {
        text: "Think about how to redesign it better",
        traits: { creative: 4, technical: 1 },
      },
    ],
  },
  {
    id: 26,
    question: "When learning something new, you prefer…",
    emoji: "📖",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      {
        text: "Hands-on practice — build and experiment",
        traits: { technical: 5 },
      },
      {
        text: "Reading theory and understanding concepts deeply",
        traits: { analytical: 5 },
      },
      {
        text: "Watching videos and visual tutorials",
        traits: { creative: 3, technical: 2 },
      },
      {
        text: "Discussion and group study with friends",
        traits: { social: 5 },
      },
    ],
  },
  {
    id: 27,
    question: "Which of these excites you the most?",
    emoji: "🚀",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      {
        text: "Building a robot that can talk and learn",
        traits: { technical: 5, analytical: 2 },
      },
      {
        text: "Directing a short film that goes viral",
        traits: { creative: 5 },
      },
      {
        text: "Running a free clinic for people in need",
        traits: { social: 5 },
      },
      {
        text: "Launching a product that makes millions",
        traits: { business: 5 },
      },
    ],
  },
  {
    id: 28,
    question: "In computer class, you enjoy…",
    emoji: "💻",
    world: "Tech Workshop",
    worldColor: "#00B894",
    options: [
      { text: "Writing code and making things work", traits: { technical: 5 } },
      { text: "Designing graphics and presentations", traits: { creative: 5 } },
      {
        text: "Researching topics and analyzing information",
        traits: { analytical: 5 },
      },
      {
        text: "Making spreadsheets and organizing data",
        traits: { business: 3, analytical: 2 },
      },
    ],
  },

  // ═══════════ 💼 BUSINESS ARENA (29-35) ═══════════
  {
    id: 29,
    question:
      "You enter the Business Arena — pitches, deals, strategy boards. You're excited by…",
    emoji: "💼",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      {
        text: "The startup pitch competition happening now",
        traits: { business: 5, social: 1 },
      },
      {
        text: "The brand design showcase on the wall",
        traits: { creative: 4, business: 1 },
      },
      {
        text: "The financial models on someone's laptop",
        traits: { analytical: 4, business: 2 },
      },
      {
        text: "The social enterprise helping a community",
        traits: { social: 4, business: 2 },
      },
    ],
  },
  {
    id: 30,
    question: "At a school fair, you would…",
    emoji: "🏪",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      { text: "Run a stall and make the most sales", traits: { business: 5 } },
      {
        text: "Create decorations and poster designs",
        traits: { creative: 5 },
      },
      {
        text: "Organize the whole event logistics",
        traits: { social: 3, business: 3 },
      },
      { text: "Set up the sound system and tech", traits: { technical: 5 } },
    ],
  },
  {
    id: 31,
    question: "When you get pocket money, you usually…",
    emoji: "💰",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      { text: "Save it and plan how to grow it", traits: { business: 5 } },
      { text: "Buy art supplies or creative tools", traits: { creative: 5 } },
      { text: "Treat my friends", traits: { social: 5 } },
      {
        text: "Buy gadgets or tech accessories",
        traits: { technical: 4, business: 1 },
      },
    ],
  },
  {
    id: 32,
    question: "You admire people who…",
    emoji: "🏆",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      {
        text: "Build empires from nothing (Tata, Ambani)",
        traits: { business: 5 },
      },
      {
        text: "Discover new things (Einstein, Kalam)",
        traits: { analytical: 5 },
      },
      {
        text: "Create art that moves millions (AR Rahman)",
        traits: { creative: 5 },
      },
      { text: "Help others selflessly (Mother Teresa)", traits: { social: 5 } },
    ],
  },
  {
    id: 33,
    question: "In 10 years, you see yourself…",
    emoji: "🔮",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      { text: "Running my own successful company", traits: { business: 5 } },
      {
        text: "Working in a top research lab",
        traits: { analytical: 4, technical: 2 },
      },
      { text: "Creating content that millions enjoy", traits: { creative: 5 } },
      {
        text: "Making a real difference in people's lives",
        traits: { social: 5 },
      },
    ],
  },
  {
    id: 34,
    question: "The best way to change the world is through…",
    emoji: "🌏",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      { text: "Business and creating jobs", traits: { business: 5 } },
      {
        text: "Science and technology breakthroughs",
        traits: { technical: 3, analytical: 3 },
      },
      { text: "Art, storytelling, and media", traits: { creative: 5 } },
      { text: "Education and social work", traits: { social: 5 } },
    ],
  },
  {
    id: 35,
    question: "Final question — what does success mean to you?",
    emoji: "🎯",
    world: "Business Arena",
    worldColor: "#FDCB6E",
    options: [
      {
        text: "Wealth, influence, and building an empire",
        traits: { business: 5, analytical: 1 },
      },
      {
        text: "Discovering something nobody else has found",
        traits: { analytical: 5 },
      },
      {
        text: "Creating something beautiful that inspires",
        traits: { creative: 5 },
      },
      { text: "Helping as many people as possible", traits: { social: 5 } },
    ],
  },
];

export default quizQuestions;
